<script setup lang="ts">
import { useFetch } from '@/composables/useFetch'; // You need to create this
import { usePermissions } from '@/composables/usePermissions';
import { usePlatform } from '@/composables/usePlatform';
import router from '@/router';
import { useEnvStore } from '@/stores/envStore';
import { usePostitsStore } from '@/stores/postitsStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { useUserStore } from '@/stores/useUserStore';
import type { MenuCourse, Task } from '@/types/planning';
import { formatDate } from '@/utils/date';
import Button from 'primevue/button';
import Calendar from 'primevue/calendar';
import Checkbox from 'primevue/checkbox';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

const route = useRoute();
const postitId = computed(() => parseInt(route.params.id.toString()));
const postitsStore = usePostitsStore();
const employeeStore = useEmployeeStore();
const { can } = usePermissions();
const selectedEmployeeId = ref<number | null>(null);
const user = ref(useUserStore().user);
const currentRestaurantId: string = user.value?.restaurants[0]?.id || '';
const toast = useToast();
const confirm = useConfirm();
const isMobilePhone = usePlatform().isMobilePhone;

const { t } = useI18n();

const postit = computed(() => postitsStore.selectedPostit);

// Permission checks
// Any restaurant user can create/update postits, assign users, set date, and manage tasks
// Only admins can delete postits
const canDeletePostits = computed(() => can('postits_planning', 'delete'));

// Helper function to show permission error
const showPermissionError = () => {
  toast.add({
    severity: 'error',
    summary: t('common.error'),
    detail: t('common.noPermission'),
    life: 3000,
  });
};

const newTask = ref<Task>({
  id: -1,
  name: '',
  postit_id: postitId.value,
  recipe_step_id: null,
  dish_id: null,
  restaurant_user_id: null,
});

const { fetchData } = useFetch();

const sortedTasks = computed(() => {
  if (!postit.value?.tasks) return [];
  // Not done first, then by order
  return [...postit.value.tasks].sort((a, b) => {
    if (a.is_done !== b.is_done) return a.is_done ? 1 : -1;
    // If both are done or not done, sort by order (ascending)
    return (a.order ?? 0) - (b.order ?? 0);
  });
});

async function toggleTaskDone(task: Task) {
  if (!postit.value) return;
  const prevDone = task.is_done;
  // Optimistically update UI
  task.is_done = !task.is_done;
  // const newDone = !!task.is_done;
  try {
    await postitsStore.setTaskDone(postit.value.id, task.id, task.is_done);
    // Optionally show a toast
    // toast.add({ severity: 'success', summary: t('common.success'), detail: t('employee.postits.task.update.success'), life: 1000 });
  } catch (e) {
    console.error('Error updating task done status:', e);
    // Optionally show a toast or error
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.postits.task.update.error'),
      life: 1500,
    });
    // Revert UI
    task.is_done = prevDone;
  }
}

onMounted(async () => {
  console.log(postitId.value);
  if (!postitsStore.selectedPostit || postitsStore.selectedPostit.id !== postitId.value) {
    console.log('postitId.value', postitId.value);
    postitsStore.selectPostit(postitId.value);
    // postit.value = postitsStore.selectedPostit
  }
  await employeeStore.fetchRestaurantUsersIfNeeded();
  await loadNextPlannedDishes();
});

onUnmounted(() => {
  postitsStore.selectedPostit = null;
});

function selectStep(step: { id: number; name: string }, dishId: number) {
  newTask.value.name = step.name;
  newTask.value.recipe_step_id = step.id;
  newTask.value.dish_id = dishId;
  createTask();
}

async function createTask() {
  try {
    await postitsStore.createTask(postitId.value, newTask.value);
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.postits.task.create.success'),
      life: 1500,
    });
    newTask.value = {
      id: -1,
      name: '',
      postit_id: postitId.value,
      recipe_step_id: null,
      dish_id: null,
      restaurant_user_id: null,
    };
  } catch (e) {
    // Optionally show a toast or error
    console.error(e);
  }
}

interface PlannedMenu {
  id: number;
  name: string;
  date: string;
  menu_courses: MenuCourse[];
}

const nextPlannedMenus = ref<PlannedMenu[]>([]);
const isLoadingNext = ref(false);
const nextPage = ref(1);
const perPage = 5;
const hasMore = ref(true);

interface RecipeStep {
  id: number;
  name: string;
  // Add other properties of a recipe step if needed
}

const recipeStepsByDish = ref<Record<number, RecipeStep[]>>({});
const loadingSteps = ref<Record<number, boolean>>({});

async function fetchRecipeStepsForDish(dishId: number) {
  if (recipeStepsByDish.value[dishId]) return; // Already fetched
  loadingSteps.value[dishId] = true;
  const { data, error } = await fetchData(`${useEnvStore().apiUrl}/dishes/${dishId}/recipe`);
  if (!error && data?.steps) {
    recipeStepsByDish.value[dishId] = data.steps;
  } else if (!error && data?.recipe?.steps) {
    recipeStepsByDish.value[dishId] = data.recipe.steps;
  } else {
    recipeStepsByDish.value[dishId] = [];
  }
  loadingSteps.value[dishId] = false;
}

async function loadNextPlannedDishes() {
  if (isLoadingNext.value || !hasMore.value) return;
  isLoadingNext.value = true;
  const restaurantId = user.value?.active_restaurant?.id;
  const startDate = postitsStore.selectedPostit?.planned_date || new Date().toISOString().split('T')[0];
  const url = `${useEnvStore().apiUrl}/planning/next-planned-dishes?restaurant_id=${restaurantId}&start_date=${startDate}&per_page=${perPage}&page=${nextPage.value}`;
  const { data, error } = await fetchData(url);
  if (!error && data?.data?.length) {
    nextPlannedMenus.value.push(...data.data);
    nextPage.value += 1;
    if (data.data.length < perPage) hasMore.value = false;
  } else {
    hasMore.value = false;
  }
  isLoadingNext.value = false;
}

const expandedDishes = reactive(new Set<string>());

function getDishKey(course, dish) {
  return `${course.id}-${dish.id}`;
}

function toggleDishExpansion(course, dish) {
  const key = getDishKey(course, dish);
  if (expandedDishes.has(key)) {
    expandedDishes.delete(key);
  } else {
    expandedDishes.add(key);
    fetchRecipeStepsForDish(dish.id);
  }
}

function isDishExpanded(course, dish) {
  return expandedDishes.has(getDishKey(course, dish));
}

const groupedMenusByDate = computed(() => {
  const groups: Record<string, PlannedMenu[]> = {};
  for (const menu of nextPlannedMenus.value) {
    // Only keep menus with at least one course with a dish
    const hasDish = menu.menu_courses?.some((course) => course.dish || course.name);
    if (!hasDish) continue;
    if (!groups[menu.date]) groups[menu.date] = [];
    groups[menu.date].push(menu);
  }
  return groups;
});

async function deleteTask(taskId: number) {
  if (!postit.value) return;
  try {
    await postitsStore.deleteTask(postit.value.id, taskId);
  } catch (e) {
    // Optionally show a toast or error
    console.error(e);
  }
}

watch(
  () => postit.value?.restaurant_user_ids,
  (val) => {
    selectedEmployeeId.value = Array.isArray(val) && val.length > 0 ? val[0] : null;
  },
  { immediate: true },
);

watch(
  () => postit.value?.planned_date,
  async (plannedDate) => {
    if (plannedDate && currentRestaurantId) {
      // planningStore.resetNextPlannedMenus();
      await loadNextPlannedDishes();
    }
  },
  { immediate: false },
);

async function assignEmployee(restaurantUserId: number | null) {
  if (!postit.value) return;

  const currentAssigned =
    Array.isArray(postit.value.restaurant_user_ids) && postit.value.restaurant_user_ids.length > 0 ? postit.value.restaurant_user_ids[0] : null;

  selectedEmployeeId.value = restaurantUserId;

  try {
    if (restaurantUserId) {
      // Detach current user if different
      if (currentAssigned && currentAssigned !== restaurantUserId) {
        await postitsStore.detachUsersFromPostit(postit.value.id, [currentAssigned]);
      }
      await postitsStore.assignUsersToPostit(postit.value.id, [restaurantUserId]);
    } else if (currentAssigned) {
      // Only detach if there is a current assignment
      await postitsStore.detachUsersFromPostit(postit.value.id, [currentAssigned]);
    }
  } catch (e) {
    console.error('Error assigning employee:', e);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.postits.attachEmployeeError'),
      life: 1500,
    });
    // Optionally revert UI selection if needed
    selectedEmployeeId.value = currentAssigned;
  }
}

// Add this ref for the calendar
const plannedDate = ref<Date | null>(null);
function toISODate(val: Date | null): string | null {
  if (!val) return null;
  // If it's a Date object, use local time to avoid timezone shift
  const year = val.getFullYear();
  const month = String(val.getMonth() + 1).padStart(2, '0');
  const day = String(val.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Sync plannedDate with postit when it loads/changes
watch(
  () => postit.value?.planned_date,
  (val) => {
    plannedDate.value = val ? new Date(val) : null;
  },
  { immediate: true },
);

// Watch for changes to plannedDate and update postit
watch(plannedDate, async (newDate, oldDate) => {
  const formattedDate = toISODate(newDate);
  if (postit.value && formattedDate && formattedDate !== toISODate(oldDate) && formattedDate !== postit.value.planned_date) {
    try {
      await postitsStore.updatePostit(postit.value.id, { planned_date: formattedDate });
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('employee.postits.update.success'),
        life: 1500,
      });
    } catch (e) {
      console.error('Error updating postit planned date:', e);
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('employee.postits.update.error'),
        life: 1500,
      });
      plannedDate.value = postit.value?.planned_date ? new Date(postit.value.planned_date) : null;
    }
  }
});

const existingStepIdsByDish = computed(() => {
  const map: Record<number, Set<number>> = {};
  if (!postit.value?.tasks) return map;
  for (const task of postit.value.tasks) {
    if (task.dish_id && task.recipe_step_id) {
      if (!map[task.dish_id]) map[task.dish_id] = new Set();
      map[task.dish_id].add(task.recipe_step_id);
    }
  }
  return map;
});

async function addAllStepsToTasks(dishId: number) {
  if (!postit.value || !recipeStepsByDish.value[dishId]) return;
  // Only add steps not already present
  const missingSteps = recipeStepsByDish.value[dishId].filter((s) => !existingStepIdsByDish.value[dishId]?.has(s.id));
  if (missingSteps.length === 0) return;

  // Prepare tasks
  const tasks = missingSteps.map((step) => ({
    id: -1,
    name: step.name,
    postit_id: postit.value!.id,
    recipe_step_id: step.id,
    dish_id: dishId,
    restaurant_user_id: null,
  }));

  try {
    await postitsStore.createTasks(postit.value.id, tasks); // See next step for store method
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.postits.task.create.success'),
      life: 1500,
    });
  } catch (e) {
    console.error('Error creating tasks for recipe steps:', e);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.postits.task.create.error'),
      life: 1500,
    });
  }
}

function courseTaskExists(courseName: string) {
  return !!postit.value?.tasks?.some((task) => task.name === courseName);
}

async function createCourseTask(course: MenuCourse) {
  if (!postit.value || !course.name) return;
  if (courseTaskExists(course.name)) return;
  const task = {
    id: -1,
    name: course.name,
    postit_id: postit.value.id,
    recipe_step_id: null,
    dish_id: course.dish?.id || null,
    restaurant_user_id: null,
  };
  try {
    await postitsStore.createTask(postit.value.id, task);
    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('employee.postits.task.create.success'),
      life: 1500,
    });
  } catch (e) {
    console.error('Error creating course task:', e);
    toast.add({
      severity: 'error',
      summary: t('common.error'),
      detail: t('employee.postits.task.create.error'),
      life: 1500,
    });
  }
}

const employeeFullName = (user) => `${user.first_name} ${user.last_name}`;

async function confirmDeletePostit(event) {
  if (!canDeletePostits.value) {
    showPermissionError();
    return;
  }
  if (!postit.value) return;
  confirm.require({
    target: event?.currentTarget,
    message: t('employee.postits.delete.confirm'),
    header: t('common.confirmation'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('common.delete'),
    rejectLabel: t('common.cancel'),
    acceptClass: 'p-button-danger',
    accept: async () => {
      if (!postit.value) return;
      await postitsStore.deletePostit(postit.value.id, { push: () => router.back() });
    },
  });
}

function handleTaskClick(task: Task) {
  if (task.is_done) return;
  if (task.dish_id && task.recipe_step_id) {
    router.push(`/menu/dishes/${task.dish?.id}/recipe-steps`);
  } else if (task.dish_id) {
    router.push(`/menu/dishes/${task.dish?.id}`);
  }
}

function confirmDeleteTask(event: Event, taskId: number) {
  confirm.require({
    target: event.target as HTMLElement,
    message: t('employee.postits.task.delete.message'),
    icon: 'pi pi-exclamation-triangle',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    rejectLabel: t('common.cancel'),
    acceptLabel: t('common.delete'),
    accept: async () => {
      await deleteTask(taskId);
    },
  });
}
</script>

<template>
  <div v-if="postit" class="flex flex-col gap-8">
    <!-- INFORMATIONS POSTITS -->
    <div class="flex flex-col gap-4">
      <div class="flex w-full items-center justify-end gap-2">
        <Button
          v-if="canDeletePostits"
          icon="pi pi-trash"
          severity="danger"
          :label="t('employee.postits.delete.buttonLabel')"
          outlined
          @click="confirmDeletePostit"
        />
      </div>
      <div class="relative flex items-center justify-end gap-2">
        <Dropdown
          v-model="selectedEmployeeId"
          class="w-full"
          :option-label="employeeFullName"
          :options="employeeStore.restaurantUsers"
          option-value="restaurant_user_id"
          :placeholder="t('employee.postits.assignEmployee')"
          style="min-width: 220px"
          show-clear
          @change="assignEmployee($event.value)"
        >
          <template #option="slot">
            <span> {{ slot.option.first_name }} {{ slot.option.last_name }} </span>
          </template>
        </Dropdown>
        <!-- <div v-if="postit.restaurant_users && postit.restaurant_users.length" class="absolute left-0 flex items-center -top-3">
          <span v-for="(pos, idx) in postit.restaurant_users[0].positions" :key="pos.id"
            class="block w-4 h-4 border border-white rounded-full" :class="{ '-ml-2': idx !== 0 }"
            :style="{ backgroundColor: '#' + pos.color }" :title="pos.name"></span>
        </div> -->
        <!-- <Button v-if="selectedEmployeeId" icon="pi pi-times" class="text-red-500" @click="assignEmployee(null)"
          :title="t('employee.postits.unassignEmployee')" /> -->
      </div>
      <div class="flex items-center justify-end">
        <Calendar
v-model="plannedDate" icon-display="input" date-format="dd.mm.yy" :min-date="new Date()"
          class="w-full" :touch-u-i="isMobilePhone" />
      </div>
    </div>

    <!-- TASKS LIST -->
    <div class="flex flex-col gap-8 border-t border-surface-300 py-4 dark:border-surface-600">
      <h2 class="font-bold">
        {{ t('employee.postits.listTasks') }}
      </h2>

      <!-- EXISTING TASKS -->
      <div>
        <h2 class="py-4 font-bold text-lg">
          <!-- {{ t('employee.postits.') }} -->
          {{ t('employee.postits.task.writeTask') }}
        </h2>
        <ul v-if="postit.tasks && postit.tasks.length" class="flex flex-col gap-4 rounded-lg border border-primary-100 p-4">
          <li v-for="task in sortedTasks" :key="task.id" class="flex h-9 w-full flex-row items-center justify-between gap-2">
            <div class="flex min-w-0 flex-1 flex-col items-start">
              <!-- <label class="flex flex-col items-start w-full min-w-0 gap-0"> -->
              <div class="flex w-full min-w-0 items-center pr-2 hover:cursor-pointer">
                <div class="flex items-center pr-2" @click="toggleTaskDone(task)">
                  <Checkbox v-model="task.is_done" binary class="mr-2 flex-none" />
                </div>
                <div class="flex min-w-0 flex-1 flex-col" @click="handleTaskClick(task)">
                  <span class="min-w-0 cursor-pointer truncate" :class="{ 'text-gray-400 line-through': task.is_done }">{{
                    task.recipe_name ?? task.name
                  }}</span>
                  <span v-if="task.dish" class="min-w-0 cursor-pointer truncate text-xs italic text-slate-400">
                    <i v-if="!task.is_done" class="pi pi-arrow-up-right" /> {{ task.dish.name }}
                  </span>
                </div>
              </div>
              <!-- </label> -->
            </div>
            <Button icon="pi pi-times" text @click="confirmDeleteTask($event, task.id)" />
          </li>
          <li class="flex w-full flex-row items-center justify-between gap-2">
            <InputText
              v-model="newTask.name"
              :placeholder="t('employee.postits.form.addTaskPlaceholder')"
              :disabled="!postit"
              class="w-full min-w-40"
            />
            <!-- <Button :label="t('employee.postits.form.addTask')" :disabled="!postit" @click="createTask" size="small" /> -->
            <Button icon="px-2 pi pi-plus" :disabled="!postit" size="small" @click="createTask" />
          </li>
        </ul>
        <div v-else class="flex flex-col gap-2 p-4 text-gray-400">
          <p>
            {{ t('employee.postits.noTasks') }}
          </p>
          <div class="flex flex-row items-center gap-2">
            <InputText v-model="newTask.name" :placeholder="t('employee.postits.form.addTaskPlaceholder')" :disabled="!postit" />
            <!-- <Button :label="t('employee.postits.form.addTask')" :disabled="!postit" @click="createTask" size="small" /> -->
            <Button icon="px-2 pi pi-plus" :disabled="!postit" size="small" @click="createTask" />
          </div>
        </div>
      </div>

      <!-- SUGGESTIONS FROM DISHES -->
      <div class="border-surface-300 dark:border-surface-600">
        <h2 class="py-4 font-bold text-lg">
          {{ t('employee.postits.suggestions') }}
        </h2>
        <div class="flex flex-col gap-4">
          <div v-for="(menus, date) in groupedMenusByDate" :key="date" class="mb-8">
            <div class="mb-2 text-xs italic text-primary-700">
              {{ formatDate(new Date(date)) }}
            </div>
            <ul class="flex flex-col gap-2 rounded-lg border border-primary-100 p-4">
              <li v-for="menu in menus" :key="menu.id" class="mb-2">
                <div class="mb-1 font-semibold text-primary-700">
                  {{ menu.name }}
                </div>
                <div v-for="course in menu.menu_courses" :key="course.id" class="my-4">
                  <ul>
                    <!-- COURSES WITH A RELATED DISH -->
                    <li v-if="course.dish && course.dish.hasRecipe" class="mb-1">
                      <div
                        class="flex cursor-pointer items-center justify-between rounded py-1 font-medium transition hover:bg-gray-100"
                        @click="toggleDishExpansion(course, course.dish)"
                      >
                        <span class="px-1 font-semibold">{{ course.dish.name }}</span>
                        <i
                          :class="{
                            'pi pi-chevron-down': isDishExpanded(course, course.dish),
                            'pi pi-chevron-right': !isDishExpanded(course, course.dish),
                            'px-2 text-right': true,
                          }"
                        ></i>
                      </div>
                      <div v-if="course.dish && isDishExpanded(course, course.dish)">
                        <div
                          v-if="
                            recipeStepsByDish[course.dish.id] &&
                            recipeStepsByDish[course.dish.id].some((s) => !existingStepIdsByDish[course.dish!.id]?.has(s.id))
                          "
                          class="my-4 ml-8 flex cursor-pointer items-center justify-end gap-2 italic text-primary-700"
                          @click="addAllStepsToTasks(course.dish!.id)"
                        >
                          <!-- <Button icon="px-2 pi pi-plus" size="small"
                                :disabled="!recipeStepsByDish[course.dish.id] || recipeStepsByDish[course.dish.id].length === 0"
                                @click="addAllStepsToTasks(course.dish.id)" /> -->
                          <span class="text-sm">
                            {{ t('employee.postits.task.addAllRecipeSteps.label') }}
                          </span>
                          <i class="pi pi-plus px-2"></i>
                        </div>
                        <div v-if="loadingSteps[course.dish.id]" class="ml-8 py-2 text-gray-400">
                          <i class="pi pi-spin pi-spinner mr-2"></i>
                          {{ t('employee.postits.loadingSteps') }}
                        </div>
                        <ul v-else-if="recipeStepsByDish[course.dish.id]" class="flex flex-col gap-0">
                          <li
                            v-for="step in recipeStepsByDish[course.dish.id].filter((s) => !existingStepIdsByDish[course.dish!.id]?.has(s.id))"
                            :key="step.id"
                            class="flex cursor-pointer items-center justify-between gap-3 rounded-sm border-surface-200 py-[10px] pl-4 hover:bg-primary-50"
                            @click.stop="selectStep(step, course.dish.id)"
                          >
                            <span class="ml-2 text-sm group-hover:underline">{{ step.name }}</span>
                            <i class="pi pi-plus px-2"></i>
                          </li>
                          <li
                            v-if="recipeStepsByDish[course.dish.id].filter((s) => !existingStepIdsByDish[course.dish!.id]?.has(s.id)).length === 0"
                            class="py-2 pl-8 text-xs italic text-gray-400"
                          >
                            {{ t('employee.postits.allStepsWereAdded') }}
                          </li>
                        </ul>
                      </div>
                    </li>

                    <!-- COURSES WITHOUT A RELATED DISH -->
                    <li v-else-if="course.name && !courseTaskExists(course.name)">
                      <div
                        class="flex cursor-pointer flex-row items-center justify-between gap-2 rounded py-1 font-medium transition hover:bg-gray-100"
                        @click="createCourseTask(course)"
                      >
                        <span class="px-1 font-semibold">{{ course.name }}</span>
                        <i v-if="!courseTaskExists(course.name)" class="pi pi-plus px-2" @click.stop="createCourseTask(course)"></i>
                      </div>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div class="flex items-center justify-center">
          <Button
            v-if="hasMore"
            :loading="isLoadingNext"
            text
            icon="pi pi-angle-down"
            :label="t('employee.postits.loadNextDishes')"
            @click="loadNextPlannedDishes"
          />
        </div>
      </div>
    </div>
  </div>
</template>
